import { useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";

import { Switch } from "../components/Switch";
import {
  ALL_COMPANY_AUDIENCE,
  EMPTY_FEED_POST_DRAFT,
  decodeAudienceValue,
  encodeAudienceValue,
  publishFeedPost,
  validateFeedPostDraft,
  type FeedAudienceOption,
  type FeedPostDraft,
} from "../data/feed";

// Feed authoring (lock 2.9: console first, admin/supervisor by permission). One clear form -
// title, body, audience, pin, require-ack - and nothing else. Feed is a bulletin surface, so the
// composer deliberately has no reply, thread, or channel affordance.
//
// This component only renders behind feed.manage; the server enforces it regardless.

export function FeedComposer({
  client,
  accountId,
  audienceOptions,
  audienceNote,
  onPublished,
}: {
  readonly client: SupabaseClient;
  readonly accountId: string;
  readonly audienceOptions: readonly FeedAudienceOption[];
  /** Honest note when the site list could not be read, so "All company only" is explained. */
  readonly audienceNote: string | null;
  readonly onPublished: () => void;
}) {
  const [draft, setDraft] = useState<FeedPostDraft>(EMPTY_FEED_POST_DRAFT);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<string | null>(null);

  const selectedValue = encodeAudienceValue({
    kind: draft.audienceKind,
    id: draft.audienceId,
    label: "",
  });

  function update(patch: Partial<FeedPostDraft>): void {
    setDraft((current) => ({ ...current, ...patch }));
    setFailure(null);
    setReceipt(null);
  }

  async function submit(): Promise<void> {
    if (busy) return;
    const validation = validateFeedPostDraft(draft);
    if (!validation.ok) {
      setFailure(validation.reason);
      return;
    }

    setBusy(true);
    setFailure(null);
    setReceipt(null);
    const outcome = await publishFeedPost(client, accountId, draft);
    setBusy(false);
    if (!outcome.ok) {
      setFailure(outcome.reason);
      return;
    }
    setDraft(EMPTY_FEED_POST_DRAFT);
    setReceipt("Posted to the Feed.");
    onPublished();
  }

  return (
    <form className="feed-composer" aria-label="New Feed post" onSubmit={(event) => event.preventDefault()}>
      <label className="feed-composer__field">
        <span className="feed-composer__label">Title</span>
        <input
          type="text"
          value={draft.title}
          disabled={busy}
          maxLength={140}
          onChange={(event) => update({ title: event.target.value })}
        />
      </label>

      <label className="feed-composer__field">
        <span className="feed-composer__label">Post</span>
        <textarea
          rows={5}
          value={draft.body}
          disabled={busy}
          onChange={(event) => update({ body: event.target.value })}
        />
      </label>

      <label className="feed-composer__field">
        <span className="feed-composer__label">Audience</span>
        <select
          value={selectedValue}
          disabled={busy}
          onChange={(event) => {
            const option = decodeAudienceValue(event.target.value, audienceOptions);
            update({ audienceKind: option.kind, audienceId: option.id });
          }}
        >
          {audienceOptions.map((option) => (
            <option key={encodeAudienceValue(option)} value={encodeAudienceValue(option)}>
              {option.label}
            </option>
          ))}
        </select>
      </label>
      {audienceNote !== null ? <p className="feed-composer__note">{audienceNote}</p> : null}

      <div className="so-switch-stack">
        <Switch
          checked={draft.isPinned}
          disabled={busy}
          label="Pin to the top"
          hint="Pinned is a badge and a position, not a colour. Colour in this product means severity."
          onChange={(isPinned) => update({ isPinned })}
        />
        <Switch
          checked={draft.requiresAck}
          disabled={busy}
          label="Require acknowledgement"
          hint="Guards must open the post and acknowledge it. Outstanding names show on the post below."
          onChange={(requiresAck) => update({ requiresAck, ackDueAt: requiresAck ? draft.ackDueAt : null })}
        />
      </div>

      {draft.requiresAck ? (
        <label className="feed-composer__field feed-composer__field--inline">
          <span className="feed-composer__label">Due (optional)</span>
          <input
            type="datetime-local"
            value={toLocalInput(draft.ackDueAt)}
            disabled={busy}
            onChange={(event) => update({ ackDueAt: fromLocalInput(event.target.value) })}
          />
        </label>
      ) : null}

      <div className="policy__editor-actions">
        <button type="button" className="minter__confirm-go" disabled={busy} onClick={() => void submit()}>
          {busy ? "Posting..." : "Post to Feed"}
        </button>
        <button
          type="button"
          className="builder__navbtn"
          disabled={busy}
          onClick={() => {
            setDraft(EMPTY_FEED_POST_DRAFT);
            setFailure(null);
            setReceipt(null);
          }}
        >
          Clear
        </button>
      </div>

      {failure !== null ? (
        <p className="surface__state surface__state--error" role="alert">
          {failure}
        </p>
      ) : null}
      {receipt !== null ? (
        <p className="surface__state surface__state--ok" role="status">
          {receipt}
        </p>
      ) : null}
    </form>
  );
}

/** `datetime-local` wants "YYYY-MM-DDTHH:MM" with no zone; storage stays ISO/UTC. */
export function toLocalInput(iso: string | null): string {
  if (iso === null) return "";
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return "";
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}T${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`;
}

export function fromLocalInput(value: string): string | null {
  if (value.trim().length === 0) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
}

export { ALL_COMPANY_AUDIENCE };
